import { useState, useEffect } from "react";
import { subirDatos, bajarDatos, eliminarDato } from "./syncSupabase";
import BotonMapaProceso from "./BotonMapaProceso";
import CampanaNotificaciones from "./CampanaNotificaciones";
import { verificarNotificacionesReacciones } from "./NotificadorALQOS";
import { authHeader } from './supabase';

const COLUMNAS_KANBAN = [
  { key:'pendiente',   label:'Pendiente',   color:'#6B7280', bg:'#F3F4F6', emoji:'📌' },
  { key:'en_proceso',  label:'En proceso',  color:'#2A7FFF', bg:'#dbeafe', emoji:'⚙️' },
  { key:'bloqueado',   label:'Bloqueado',   color:'#dc2626', bg:'#fee2e2', emoji:'🚫' },
  { key:'listo',       label:'Listo',       color:'#16a34a', bg:'#dcfce7', emoji:'✅' },
];

const MODULOS_VINCULO = ['NC','FMEA','Auditoría','Acción Correctiva','Plan de Control','HACCP','POEs','TPM','Otro'];
const PRIORIDADES = { alta:{ label:'Alta', color:'#dc2626', bg:'#fee2e2' }, media:{ label:'Media', color:'#d97706', bg:'#fef3c7' }, baja:{ label:'Baja', color:'#16a34a', bg:'#dcfce7' } };
const COLORES_STICKER = ['#fef3c7','#dcfce7','#dbeafe','#f3e8ff','#ffe4e6','#e0f2fe'];

function diasDesde(fecha) {
  if (!fecha) return 0;
  return Math.floor((Date.now() - new Date(fecha).getTime()) / 86400000);
}

const TAG_STYLE_CM = {
  'AUDITORÍA':         { color:'#3ECF8E', bg:'rgba(62,207,142,.12)' },
  'CERTIFICACIÓN':     { color:'#2A7FFF', bg:'rgba(42,127,255,.14)' },
  'ACCIÓN CORRECTIVA': { color:'#b3b6ee', bg:'rgba(107,111,214,.16)' },
  'RACHA':             { color:'#8fa8ff', bg:'rgba(28,63,170,.22)' },
  'CUMPLEAÑOS':        { color:'#f9a8d4', bg:'rgba(249,168,212,.14)' },
  'COMUNICADO':        { color:'#EDEFF7', bg:'rgba(232,233,246,.10)' },
  'MANUAL':            { color:'#EDEFF7', bg:'rgba(232,233,246,.10)' },
};
const RACHA_UMBRAL_DIAS = 30;
const EMOJIS_NOTICIA = ['✅','📜','🔒','🔥','📣','🎉','🎂','🏆','📚'];

// Gauges "meter": relación contra un objetivo, calculados al vuelo desde
// alqos_ncs/alqos_acciones_correctivas/alqos_auditorias ya sincronizados —
// sin tabla nueva. Campos reales verificados en NoConformidades.js,
// AccionesCorrectivas.js y Auditorias.js antes de escribir esto (no inventados):
// NC.estado∈{abierta,en_analisis,en_proceso,cerrada}, NC.severidad∈{critica,mayor,menor};
// AC.estado∈{abierta,en_proceso,implementada,verificada,cerrada}, AC.resultadoEficacia∈{eficaz,no_eficaz,pendiente};
// Auditoria.estado∈{planificada,en_curso,completada,cancelada}, Auditoria.hallazgos:[].
function calcularGauges(ncs, acciones, auditorias) {
  const ncsCerradas = ncs.filter(n => n.estado === 'cerrada');
  const ncsConPlazo = ncsCerradas.filter(n => n.fechaCompromiso);
  const ncsEnPlazo = ncsConPlazo.filter(n => n.fechaCierre && new Date(n.fechaCierre) <= new Date(n.fechaCompromiso));
  const pctNcPlazo = ncsConPlazo.length > 0 ? Math.round((ncsEnPlazo.length / ncsConPlazo.length) * 100) : null;

  const accionesCerradas = acciones.filter(a => a.estado === 'cerrada');
  const accionesEficaces = accionesCerradas.filter(a => a.resultadoEficacia === 'eficaz');
  const pctAccEficaces = accionesCerradas.length > 0 ? Math.round((accionesEficaces.length / accionesCerradas.length) * 100) : null;

  const audCompletadas = auditorias.filter(a => a.estado === 'completada');
  const audSinHallazgos = audCompletadas.filter(a => (a.hallazgos || []).length === 0);
  const pctAudSinHallazgos = audCompletadas.length > 0 ? Math.round((audSinHallazgos.length / audCompletadas.length) * 100) : null;

  const ncsCriticas = ncs.filter(n => n.severidad === 'critica' && n.fecha).sort((a,b) => new Date(b.fecha) - new Date(a.fecha));
  const ultimaCritica = ncsCriticas[0] || null;
  const rachaDias = ultimaCritica ? diasDesde(ultimaCritica.fecha) : null;

  return { pctNcPlazo, pctAccEficaces, pctAudSinHallazgos, rachaDias, ultimaCritica };
}

function estadoGauge(pct) {
  if (pct === null) return { color:'#6B7280', icon:'—', texto:'Sin datos suficientes' };
  if (pct >= 90) return { color:'#0ca30c', icon:'✔', texto:'Objetivo cumplido' };
  if (pct >= 70) return { color:'#fab219', icon:'!', texto:'Cerca del objetivo' };
  return { color:'#d03b3b', icon:'✕', texto:'Requiere atención' };
}

// Candidatos: puramente calculados en el cliente, nunca se escriben en la
// base hasta que alguien hace clic en "Publicar →". yaPublicado evita
// re-sugerir algo que ya está en alqos_news (dedup por fuente+origenId).
function calcularCandidatos(ncs, acciones, auditorias, empleados, publicados) {
  const yaPublicado = (fuente, origenId) => publicados.some(p => p.fuente === fuente && p.origenId === origenId);
  const candidatos = [];

  auditorias
    .filter(a => a.estado === 'completada' && (a.hallazgos || []).length === 0 && !yaPublicado('auditorias', a.id))
    .forEach(a => candidatos.push({
      fuente:'auditorias', origenId:a.id, icono:'✅', tag:'AUDITORÍA',
      titulo:`Auditoría ${a.codigo || ''} cerrada sin hallazgos`.trim(),
      descripcion:[a.tipo, a.norma, a.fecha].filter(Boolean).join(' · '),
    }));

  acciones
    .filter(a => a.estado === 'cerrada' && a.resultadoEficacia === 'eficaz' && !yaPublicado('acciones', a.id))
    .forEach(a => candidatos.push({
      fuente:'acciones', origenId:a.id, icono:'🔒', tag:'ACCIÓN CORRECTIVA',
      titulo:`${a.idAccion || 'Acción correctiva'} cerrada — eficaz`,
      descripcion:(a.descripcionAccion || '').slice(0, 140),
    }));

  const ncsCriticas = ncs.filter(n => n.severidad === 'critica' && n.fecha).sort((a,b) => new Date(b.fecha) - new Date(a.fecha));
  const ultimaCritica = ncsCriticas[0] || null;
  const rachaDias = ultimaCritica ? diasDesde(ultimaCritica.fecha) : null;
  const origenRacha = ultimaCritica ? ultimaCritica.id : -1; // -1 = nunca hubo NC crítica registrada
  if (rachaDias !== null && rachaDias >= RACHA_UMBRAL_DIAS && !yaPublicado('ncs', origenRacha)) {
    candidatos.push({
      fuente:'ncs', origenId:origenRacha, icono:'🔥', tag:'RACHA',
      titulo:`${rachaDias} días sin NC crítica`,
      descripcion:'Racha activa sin no conformidades de severidad crítica.',
    });
  }

  // Cumpleaños: detectado solo, publicado igual que todo lo demás — SIEMPRE pasa por
  // el botón "Publicar →", sin excepción. Solo excluye empleados dados de baja
  // (estado "inactivo"); "licencia" sigue contando como activo en la empresa.
  const hoy = new Date();
  const hoyISO = hoy.toISOString().slice(0, 10);
  empleados
    .filter(e => e.estado !== 'inactivo' && e.fechaNacimiento)
    .filter(e => {
      // fechaNacimiento es "YYYY-MM-DD" (input type=date) — Date la parsea como
      // medianoche UTC, así que hay que leerla de vuelta con getUTC*() para no
      // correrse un día en timezones negativos (UTC-3) al comparar con getMonth/
      // getDate() LOCAL de "hoy", que sí es lo que corresponde para "hoy" real.
      const fn = new Date(e.fechaNacimiento);
      return fn.getUTCMonth() === hoy.getMonth() && fn.getUTCDate() === hoy.getDate();
    })
    .forEach(e => {
      const origenId = `${e.id}-${hoyISO}`; // por día, para que reaparezca cada cumpleaños siguiente
      if (yaPublicado('rrhh', origenId)) return;
      candidatos.push({
        fuente:'rrhh', origenId, icono:'🎂', tag:'CUMPLEAÑOS',
        titulo:`Hoy es el cumpleaños de ${e.nombre}`,
        descripcion:[e.puesto, e.area].filter(Boolean).join(' · '),
      });
    });

  return candidatos;
}

function GaugeCircular({ value, unit, label, statusColor, statusText, statusIcon, max = 100 }) {
  const r = 40, stroke = 9, C = 2 * Math.PI * r;
  const pct = value === null ? 0 : Math.min(1, value / max);
  const dash = C * pct;
  return (
    <div style={{ background:'linear-gradient(180deg, #20242c 0%, #262b34 100%)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'16px', padding:'14px 12px 12px', display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center' }}>
      <svg width="104" height="104" viewBox="0 0 104 104">
        <circle cx="52" cy="52" r={r} fill="none" stroke="#33406b" strokeWidth={stroke}/>
        <circle cx="52" cy="52" r={r} fill="none" stroke={statusColor} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${dash} ${C - dash}`} transform="rotate(-90 52 52)"/>
        <text x="52" y="49" textAnchor="middle" fontSize="18" fontWeight="700" fill="#EDEFF7">{value === null ? '—' : value}</text>
        <text x="52" y="64" textAnchor="middle" fontSize="9" fill="#9BA3B4">{unit}</text>
      </svg>
      <div style={{ fontSize:'11.5px', color:'#c3c6d4', marginTop:'6px', fontWeight:'600' }}>{label}</div>
      <div style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'10.5px', marginTop:'4px', fontWeight:'600', color:statusColor }}>
        <span style={{ width:'6px', height:'6px', borderRadius:'50%', background:statusColor }}/>
        {statusIcon} {statusText}
      </div>
    </div>
  );
}

export default function EscritorioCalidad({ onVolver, oscuro, usuario }) {
  const [tab, setTab]               = useState('alertas');
  const [tareas, setTareas]         = useState([]);
  const [stickers, setStickers]     = useState([]);
  const [dragId, setDragId]         = useState(null);
  const [dragOver, setDragOver]     = useState(null);
  const [showFormTarea, setShowFormTarea] = useState(false);
  const [showFormSticker, setShowFormSticker] = useState(false);
  const [ariaAnalisis, setAriaAnalisis]   = useState('');
  const [ariaLoading, setAriaLoading]     = useState(false);
  const [alertas, setAlertas]       = useState({ ncs:[], acciones:[], auditorias:[], fmeas:[] });
  const [noticias, setNoticias]     = useState([]);
  const [datosCM, setDatosCM]       = useState({ ncs:[], acciones:[], auditorias:[], empleados:[] });
  const [showFormNoticia, setShowFormNoticia] = useState(false);

  const esCentroMando = usuario?.rol === 'Administrador' || usuario?.rol === 'Calidad';
  const NOTICIA_VACIA = { titulo:'', descripcion:'', tag:'MANUAL', icono:'📣' };
  const [formNoticia, setFormNoticia] = useState(NOTICIA_VACIA);

  const TAREA_VACIA = { titulo:'', descripcion:'', modulo:'NC', prioridad:'media', responsable:'', fechaLimite:'', estado:'pendiente' };
  const [form, setForm] = useState(TAREA_VACIA);
  const [stickerColor, setStickerColor] = useState(COLORES_STICKER[0]);
  const [stickerTexto, setStickerTexto] = useState('');

  useEffect(() => {
    const cargar = async () => {
      await bajarDatos('alqos_escritorio_calidad_tareas');
      await bajarDatos('alqos_escritorio_calidad_stickers');
      if (esCentroMando) await bajarDatos('alqos_news');
      try { setTareas(JSON.parse(localStorage.getItem('alqos_escritorio_calidad_tareas') || '[]')); } catch { setTareas([]); }
      try { setStickers(JSON.parse(localStorage.getItem('alqos_escritorio_calidad_stickers') || '[]')); } catch { setStickers([]); }
      try { setNoticias(JSON.parse(localStorage.getItem('alqos_news') || '[]')); } catch { setNoticias([]); }
      // Cargar alertas desde módulos existentes
      try {
        const ncs = JSON.parse(localStorage.getItem('alqos_ncs') || '[]');
        const acciones = JSON.parse(localStorage.getItem('alqos_acciones_correctivas') || '[]');
        const auditoriasProgramadas = JSON.parse(localStorage.getItem('alqos_programa_auditorias') || '[]');
        const auditorias = JSON.parse(localStorage.getItem('alqos_auditorias') || '[]');
        const empleados = JSON.parse(localStorage.getItem('alqos_rrhh') || '[]');
        const fmeas = JSON.parse(localStorage.getItem('alqos_fmea') || '[]');
        setAlertas({
          ncs: ncs.filter(n => n.estado !== 'cerrada' && n.estado !== 'cerrado' && diasDesde(n.fecha) > 7),
          acciones: acciones.filter(a => a.estado !== 'cerrada' && a.estado !== 'cerrado' && a.estado !== 'verificada'),
          auditorias: auditoriasProgramadas.filter(a => { try { return new Date(a.fecha) <= new Date(Date.now() + 7*86400000); } catch { return false; } }),
          fmeas: fmeas.filter(f => Number(f.npr || f.npd || 0) >= 100),
        });
        setDatosCM({ ncs, acciones, auditorias, empleados });
      } catch {}
      if (esCentroMando) verificarNotificacionesReacciones();
    };
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const guardarTareas = (lista) => { setTareas(lista); localStorage.setItem('alqos_escritorio_calidad_tareas', JSON.stringify(lista)); subirDatos('alqos_escritorio_calidad_tareas'); };
  const guardarStickers = (lista) => { setStickers(lista); localStorage.setItem('alqos_escritorio_calidad_stickers', JSON.stringify(lista)); subirDatos('alqos_escritorio_calidad_stickers'); };
  const guardarNoticias = (lista) => { setNoticias(lista); localStorage.setItem('alqos_news', JSON.stringify(lista)); subirDatos('alqos_news'); window.dispatchEvent(new Event('alqos-news-actualizado')); };

  const publicarCandidato = (candidato) => {
    const nueva = {
      id: Math.floor(Date.now()), tipo:'auto', fuente:candidato.fuente, origenId:candidato.origenId,
      titulo:candidato.titulo, descripcion:candidato.descripcion, tag:candidato.tag, icono:candidato.icono,
      estado:'publicado', publicadoPor: usuario?.nombre || 'Sistema',
      fechaPublicacion: new Date().toISOString(), reacciones:{},
    };
    guardarNoticias([nueva, ...noticias]);
  };

  const publicarNoticiaManual = () => {
    if (!formNoticia.titulo.trim()) return;
    const nueva = {
      id: Math.floor(Date.now()), tipo:'manual', fuente:'manual', origenId:null,
      titulo:formNoticia.titulo, descripcion:formNoticia.descripcion, tag:formNoticia.tag, icono:formNoticia.icono || '📣',
      estado:'publicado', publicadoPor: usuario?.nombre || 'Sistema',
      fechaPublicacion: new Date().toISOString(), reacciones:{},
    };
    guardarNoticias([nueva, ...noticias]);
    setFormNoticia(NOTICIA_VACIA); setShowFormNoticia(false);
  };

  const gauges = calcularGauges(datosCM.ncs, datosCM.acciones, datosCM.auditorias);
  const candidatos = calcularCandidatos(datosCM.ncs, datosCM.acciones, datosCM.auditorias, datosCM.empleados, noticias);
  const publicadosOrdenados = [...noticias].sort((a,b) => new Date(b.fechaPublicacion) - new Date(a.fechaPublicacion));

  const agregarTarea = () => {
    if (!form.titulo.trim()) return;
    const nueva = { ...form, id: Date.now(), fechaCreacion: new Date().toISOString().slice(0,10), creadoPor: usuario?.nombre || 'Sistema' };
    guardarTareas([nueva, ...tareas]);
    setForm(TAREA_VACIA); setShowFormTarea(false);
  };

  const agregarSticker = () => {
    if (!stickerTexto.trim()) return;
    const nuevo = { id: Date.now(), texto: stickerTexto, color: stickerColor, fecha: new Date().toISOString().slice(0,10), usuario: usuario?.nombre || 'Sistema' };
    guardarStickers([nuevo, ...stickers]);
    setStickerTexto(''); setShowFormSticker(false);
  };

  const onDrop = (col) => {
    if (!dragId) return;
    const lista = tareas.map(t => t.id === dragId ? { ...t, estado: col } : t);
    guardarTareas(lista); setDragId(null); setDragOver(null);
  };

  const eliminarTarea = async (id) => {
    const lista = tareas.filter(t => t.id !== id);
    setTareas(lista);
    localStorage.setItem('alqos_escritorio_calidad_tareas', JSON.stringify(lista));
    await eliminarDato('alqos_escritorio_calidad_tareas', id);
  };
  const eliminarSticker = async (id) => {
    const lista = stickers.filter(s => s.id !== id);
    setStickers(lista);
    localStorage.setItem('alqos_escritorio_calidad_stickers', JSON.stringify(lista));
    await eliminarDato('alqos_escritorio_calidad_stickers', id);
  };

  const analizarConAria = async () => {
    setAriaLoading(true); setAriaAnalisis('');
    try {
      const contexto = {
        tareas_pendientes: tareas.filter(t => t.estado === 'pendiente').length,
        tareas_bloqueadas: tareas.filter(t => t.estado === 'bloqueado').length,
        ncs_abiertas: alertas.ncs.length,
        acciones_pendientes: alertas.acciones.length,
        auditorias_proximas: alertas.auditorias.length,
        fmeas_criticos: alertas.fmeas.length,
        detalle_tareas: tareas.filter(t => t.estado !== 'listo').map(t => ({ titulo: t.titulo, prioridad: t.prioridad, modulo: t.modulo, dias: diasDesde(t.fechaCreacion) })),
      };
      const response = await fetch('/api/aria', {
        method: 'POST',
        headers: { ...(await authHeader()), 'Content-Type': 'application/json', 'anthropic-version': '2023-06-01', },
        body: JSON.stringify({
          model: 'claude-sonnet-4-5',
          max_tokens: 1000,
          system: 'Sos ARIA, asistente de ALQOS para pymes graficas industriales argentinas. Jefa de Calidad usa este escritorio para gestionar el sistema ISO. Respondé directo, en español, sin vueltas.',
          messages: [{ role: 'user', content: 'Analizá el estado del sistema de calidad y dame: 1) Semáforo general (🟢🟡🔴) con una línea de diagnóstico, 2) Top 3 acciones urgentes para HOY, 3) Qué riesgos ves si no se actúa esta semana. Datos: ' + JSON.stringify(contexto, null, 2) + '. Máximo 200 palabras. Bullets cortos.' }],
        }),
      });
      const data = await response.json();
      setAriaAnalisis(data.content?.[0]?.text || 'Sin respuesta.');
    } catch { setAriaAnalisis('Error al conectar con ARIA.'); }
    setAriaLoading(false);
  };

  const totalAlertas = alertas.ncs.length + alertas.acciones.length + alertas.auditorias.length + alertas.fmeas.length;
  const inp = { padding:'8px 10px', borderRadius:'8px', border:'1px solid #E5E7EB', fontSize:'13px', fontFamily:'Inter, sans-serif', outline:'none', width:'100%', boxSizing:'border-box' };
  const lbl = { fontSize:'11px', fontWeight:'600', color:'#6B7280', display:'block', marginBottom:'4px' };

  return (
    <div style={{ padding:'24px', fontFamily:'Inter, sans-serif', background:'#E8E9F6', minHeight:'100vh' }}>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
          <button onClick={onVolver} style={{ background:'none', border:'1px solid #E5E7EB', borderRadius:'8px', padding:'6px 12px', fontSize:'12px', cursor:'pointer', color:'#6B7280' }}>← Menú</button>
          <span style={{ fontSize:'22px' }}>🗂️</span>
          <div>
            <h1 style={{ fontSize:'20px', fontWeight:'700', color:'#1C3FAA', margin:0 }}>Escritorio de Calidad</h1>
            <p style={{ fontSize:'12px', color:'#6B7280', margin:0 }}>Panel de trabajo del equipo · ISO 9001 · {usuario?.nombre || ''}</p>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
          {esCentroMando && (
            <div style={{ background:'#09184D', borderRadius:'10px' }}>
              <CampanaNotificaciones oscuro={oscuro} modulo="AlqosNews"/>
            </div>
          )}
          <button onClick={analizarConAria} disabled={ariaLoading}
            style={{ padding:'10px 16px', borderRadius:'10px', border:'none', background:ariaLoading?'#E8E9F6':'#09184D', color:ariaLoading?'#6B7280':'white', fontSize:'12px', fontWeight:'600', cursor:ariaLoading?'wait':'pointer' }}>
            {ariaLoading ? '🤖 Analizando...' : '🤖 ARIA — Estado del sistema'}
          </button>
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="escritorio_calidad" oscuro={oscuro} />}
        </div>
      </div>

      {ariaAnalisis && (
        <div style={{ background:'linear-gradient(135deg,#09184D,#1a2f7a)', borderRadius:'12px', padding:'16px 20px', marginBottom:'16px', color:'white' }}>
          <div style={{ fontSize:'10px', color:'rgba(255,255,255,0.55)', marginBottom:'8px', fontWeight:'700', letterSpacing:'0.12em', textTransform:'uppercase' }}>🤖 ARIA — Diagnóstico del sistema de calidad</div>
          <div style={{ fontSize:'13px', lineHeight:'1.7', color:'rgba(255,255,255,0.9)', whiteSpace:'pre-line' }}>{ariaAnalisis}</div>
          <button onClick={() => setAriaAnalisis('')} style={{ marginTop:'10px', background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:'6px', padding:'4px 10px', fontSize:'11px', color:'rgba(255,255,255,0.7)', cursor:'pointer' }}>Cerrar</button>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px', marginBottom:'16px' }}>
        {[
          { label:'Tareas pendientes', valor: tareas.filter(t=>t.estado==='pendiente').length, color:'#6B7280' },
          { label:'En proceso',        valor: tareas.filter(t=>t.estado==='en_proceso').length, color:'#2A7FFF' },
          { label:'Bloqueadas',         valor: tareas.filter(t=>t.estado==='bloqueado').length, color:'#dc2626' },
          { label:'Alertas sistema',    valor: totalAlertas, color: totalAlertas>0?'#d97706':'#16a34a' },
        ].map((m,i) => (
          <div key={i} style={{ background:'white', borderRadius:'12px', padding:'12px 14px', border:'1px solid #E5E7EB' }}>
            <div style={{ fontSize:'11px', color:'#6B7280', marginBottom:'4px' }}>{m.label}</div>
            <div style={{ fontSize:'24px', fontWeight:'700', color:m.color }}>{m.valor}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'flex', gap:'6px', marginBottom:'16px', background:'white', padding:'4px', borderRadius:'10px', border:'1px solid #E5E7EB', width:'fit-content' }}>
        {[['alertas',`🔔 Alertas${totalAlertas>0?' ('+totalAlertas+')':''}`],['kanban','🗂 Kanban'],['stickers','📌 Post-its'],...(esCentroMando?[['novedades',`📰 Centro de Mando${candidatos.length>0?' ('+candidatos.length+')':''}`]]:[])].map(([id,lb]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding:'6px 16px', borderRadius:'8px', border:'none', fontSize:'12px', fontWeight:'600', cursor:'pointer', background:tab===id?'#1C3FAA':'transparent', color:tab===id?'white':'#6B7280' }}>{lb}</button>
        ))}
      </div>

      {tab === 'alertas' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
          <div style={{ background:'white', borderRadius:'12px', padding:'16px', border:`1px solid ${alertas.ncs.length?'#fecaca':'#E5E7EB'}` }}>
            <div style={{ fontSize:'11px', fontWeight:'700', color:alertas.ncs.length?'#dc2626':'#6B7280', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.08em' }}>⚠️ NCs abiertas +7 días ({alertas.ncs.length})</div>
            {alertas.ncs.length === 0
              ? <div style={{ textAlign:'center', padding:'20px', color:'#D1D5DB', fontSize:'12px' }}>Sin NCs pendientes 🎉</div>
              : alertas.ncs.slice(0,5).map((nc,i) => (
                <div key={i} style={{ padding:'7px 0', borderBottom:'1px solid #F3F4F6', fontSize:'12px' }}>
                  <div style={{ fontWeight:'600', color:'#374151' }}>{nc.codigo || nc.numero || 'NC sin código'}</div>
                  <div style={{ fontSize:'11px', color:'#6B7280' }}>{nc.descripcion?.slice(0,60) || nc.titulo?.slice(0,60) || '—'}</div>
                  <div style={{ fontSize:'10px', color:'#dc2626', marginTop:'2px' }}>{diasDesde(nc.fecha)} días abierta</div>
                </div>
              ))
            }
          </div>
          <div style={{ background:'white', borderRadius:'12px', padding:'16px', border:`1px solid ${alertas.acciones.length?'#fde68a':'#E5E7EB'}` }}>
            <div style={{ fontSize:'11px', fontWeight:'700', color:alertas.acciones.length?'#d97706':'#6B7280', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.08em' }}>🔁 Acciones correctivas pendientes ({alertas.acciones.length})</div>
            {alertas.acciones.length === 0
              ? <div style={{ textAlign:'center', padding:'20px', color:'#D1D5DB', fontSize:'12px' }}>Sin acciones pendientes 🎉</div>
              : alertas.acciones.slice(0,5).map((a,i) => (
                <div key={i} style={{ padding:'7px 0', borderBottom:'1px solid #F3F4F6', fontSize:'12px' }}>
                  <div style={{ fontWeight:'600', color:'#374151' }}>{a.codigo || a.titulo || 'AC sin código'}</div>
                  <div style={{ fontSize:'11px', color:'#6B7280' }}>{a.descripcion?.slice(0,60) || '—'}</div>
                  <div style={{ fontSize:'10px', color:'#d97706', marginTop:'2px' }}>Estado: {a.estado || 'sin estado'}</div>
                </div>
              ))
            }
          </div>
          <div style={{ background:'white', borderRadius:'12px', padding:'16px', border:`1px solid ${alertas.auditorias.length?'#c4b5fd':'#E5E7EB'}` }}>
            <div style={{ fontSize:'11px', fontWeight:'700', color:alertas.auditorias.length?'#7c3aed':'#6B7280', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.08em' }}>📅 Auditorías próximas 7 días ({alertas.auditorias.length})</div>
            {alertas.auditorias.length === 0
              ? <div style={{ textAlign:'center', padding:'20px', color:'#D1D5DB', fontSize:'12px' }}>Sin auditorías próximas</div>
              : alertas.auditorias.slice(0,5).map((a,i) => (
                <div key={i} style={{ padding:'7px 0', borderBottom:'1px solid #F3F4F6', fontSize:'12px' }}>
                  <div style={{ fontWeight:'600', color:'#374151' }}>{a.titulo || a.tipo || 'Auditoría'}</div>
                  <div style={{ fontSize:'10px', color:'#7c3aed', marginTop:'2px' }}>📅 {a.fecha}</div>
                </div>
              ))
            }
          </div>
          <div style={{ background:'white', borderRadius:'12px', padding:'16px', border:`1px solid ${alertas.fmeas.length?'#fecaca':'#E5E7EB'}` }}>
            <div style={{ fontSize:'11px', fontWeight:'700', color:alertas.fmeas.length?'#dc2626':'#6B7280', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.08em' }}>🔍 FMEAs con NPR crítico ({alertas.fmeas.length})</div>
            {alertas.fmeas.length === 0
              ? <div style={{ textAlign:'center', padding:'20px', color:'#D1D5DB', fontSize:'12px' }}>Sin FMEAs críticos 🎉</div>
              : alertas.fmeas.slice(0,5).map((f,i) => (
                <div key={i} style={{ padding:'7px 0', borderBottom:'1px solid #F3F4F6', fontSize:'12px' }}>
                  <div style={{ fontWeight:'600', color:'#374151' }}>{f.proceso || f.titulo || 'FMEA'}</div>
                  <div style={{ fontSize:'10px', color:'#dc2626', marginTop:'2px' }}>NPR: {f.npr || f.npd || '≥100'}</div>
                </div>
              ))
            }
          </div>
        </div>
      )}

      {tab === 'kanban' && (
        <div>
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:'12px' }}>
            <button onClick={() => setShowFormTarea(true)} style={{ padding:'9px 18px', borderRadius:'10px', border:'none', background:'#1C3FAA', color:'white', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>+ Nueva tarea</button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px' }}>
            {COLUMNAS_KANBAN.map(col => {
              const tarjetas = tareas.filter(t => t.estado === col.key);
              return (
                <div key={col.key}
                  onDragOver={e => { e.preventDefault(); setDragOver(col.key); }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={() => onDrop(col.key)}
                  style={{ background:dragOver===col.key?col.bg:'#F9FAFB', borderRadius:'12px', padding:'10px', border:`2px solid ${dragOver===col.key?col.color:'#E5E7EB'}`, minHeight:'400px', transition:'all 0.15s' }}>
                  <div style={{ marginBottom:'10px', paddingBottom:'8px', borderBottom:`2px solid ${col.color}30` }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontSize:'12px', fontWeight:'700', color:col.color }}>{col.emoji} {col.label}</span>
                      <span style={{ background:col.bg, color:col.color, fontSize:'11px', fontWeight:'700', padding:'2px 7px', borderRadius:'20px' }}>{tarjetas.length}</span>
                    </div>
                  </div>
                  {tarjetas.map(t => {
                    const pri = PRIORIDADES[t.prioridad] || PRIORIDADES.media;
                    return (
                      <div key={t.id} draggable onDragStart={() => setDragId(t.id)} onDragEnd={() => { setDragId(null); setDragOver(null); }}
                        style={{ background:'white', borderRadius:'10px', padding:'10px 12px', marginBottom:'8px', border:'1px solid #E5E7EB', cursor:'grab', opacity:dragId===t.id?0.5:1, boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'4px' }}>
                          <div style={{ fontWeight:'700', fontSize:'12px', color:'#111827', flex:1, marginRight:'6px' }}>{t.titulo}</div>
                          <button onClick={() => eliminarTarea(t.id)} style={{ background:'none', border:'none', color:'#D1D5DB', cursor:'pointer', fontSize:'12px', padding:0, flexShrink:0 }}>✕</button>
                        </div>
                        {t.descripcion && <div style={{ fontSize:'11px', color:'#6B7280', marginBottom:'5px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.descripcion}</div>}
                        <div style={{ display:'flex', gap:'4px', flexWrap:'wrap', marginTop:'5px' }}>
                          <span style={{ background:pri.bg, color:pri.color, fontSize:'10px', fontWeight:'600', padding:'2px 6px', borderRadius:'5px' }}>{pri.label}</span>
                          <span style={{ background:'#E8E9F6', color:'#1C3FAA', fontSize:'10px', fontWeight:'600', padding:'2px 6px', borderRadius:'5px' }}>{t.modulo}</span>
                        </div>
                        {t.responsable && <div style={{ fontSize:'10px', color:'#9CA3AF', marginTop:'4px' }}>👤 {t.responsable}</div>}
                        {t.fechaLimite && <div style={{ fontSize:'10px', color: new Date(t.fechaLimite) < new Date()?'#dc2626':'#6B7280', marginTop:'2px' }}>📅 {t.fechaLimite}</div>}
                      </div>
                    );
                  })}
                  {tarjetas.length === 0 && (
                    <div style={{ textAlign:'center', paddingTop:'40px', color:'#D1D5DB', fontSize:'11px' }}>
                      <div style={{ fontSize:'24px', marginBottom:'6px', opacity:0.4 }}>{col.emoji}</div>Arrastrá acá
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'stickers' && (
        <div>
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:'12px' }}>
            <button onClick={() => setShowFormSticker(true)} style={{ padding:'9px 18px', borderRadius:'10px', border:'none', background:'#1C3FAA', color:'white', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>+ Nuevo post-it</button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px,1fr))', gap:'12px' }}>
            {stickers.length === 0
              ? <div style={{ gridColumn:'1/-1', textAlign:'center', padding:'60px', color:'#D1D5DB' }}>
                  <div style={{ fontSize:'40px', marginBottom:'10px' }}>📌</div>
                  <div style={{ fontSize:'13px' }}>Sin post-its todavía</div>
                  <div style={{ fontSize:'11px', marginTop:'4px' }}>Usalos para recordatorios rápidos del equipo</div>
                </div>
              : stickers.map(s => (
                <div key={s.id} style={{ background:s.color, borderRadius:'12px', padding:'14px', minHeight:'120px', position:'relative', boxShadow:'2px 3px 8px rgba(0,0,0,0.1)', border:'1px solid rgba(0,0,0,0.06)' }}>
                  <button onClick={() => eliminarSticker(s.id)} style={{ position:'absolute', top:'8px', right:'8px', background:'rgba(0,0,0,0.1)', border:'none', borderRadius:'50%', width:'18px', height:'18px', cursor:'pointer', fontSize:'10px', display:'flex', alignItems:'center', justifyContent:'center', color:'#374151' }}>✕</button>
                  <div style={{ fontSize:'13px', color:'#374151', lineHeight:'1.5', whiteSpace:'pre-line', marginBottom:'8px', paddingRight:'16px' }}>{s.texto}</div>
                  <div style={{ fontSize:'10px', color:'rgba(55,65,81,0.6)', marginTop:'auto' }}>👤 {s.usuario} · {s.fecha}</div>
                </div>
              ))
            }
          </div>
        </div>
      )}

      {tab === 'novedades' && esCentroMando && (
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'14px', marginBottom:'20px' }}>
            <GaugeCircular value={gauges.pctNcPlazo} unit="%" label="NC cerradas en plazo" max={100}
              statusColor={estadoGauge(gauges.pctNcPlazo).color} statusIcon={estadoGauge(gauges.pctNcPlazo).icon} statusText={estadoGauge(gauges.pctNcPlazo).texto}/>
            <GaugeCircular value={gauges.pctAccEficaces} unit="%" label="Acciones correctivas eficaces" max={100}
              statusColor={estadoGauge(gauges.pctAccEficaces).color} statusIcon={estadoGauge(gauges.pctAccEficaces).icon} statusText={estadoGauge(gauges.pctAccEficaces).texto}/>
            <GaugeCircular value={gauges.pctAudSinHallazgos} unit="%" label="Auditorías sin hallazgos" max={100}
              statusColor={estadoGauge(gauges.pctAudSinHallazgos).color} statusIcon={estadoGauge(gauges.pctAudSinHallazgos).icon} statusText={estadoGauge(gauges.pctAudSinHallazgos).texto}/>
            <GaugeCircular value={gauges.rachaDias} unit="días" label="Racha sin NC crítica" max={Math.max(60, gauges.rachaDias || 0)}
              statusColor="#2A7FFF" statusIcon="🔥" statusText={gauges.rachaDias === null ? 'Sin NC crítica registrada' : 'en curso'}/>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>
            <div style={{ background:'linear-gradient(180deg, #20242c 0%, #262b34 100%)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'18px', padding:'18px 18px 10px' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'4px' }}>
                <h2 style={{ margin:0, fontSize:'14px', fontWeight:'700', color:'#EDEFF7' }}>📥 Candidatos automáticos</h2>
                <span style={{ fontSize:'11px', fontWeight:'700', color:'#EDEFF7', background:'rgba(255,255,255,.06)', padding:'2px 9px', borderRadius:'999px' }}>{candidatos.length} sin publicar</span>
              </div>
              <div style={{ fontSize:'11px', color:'#9BA3B4', margin:'-2px 0 12px' }}>Detectados solos por el sistema — nada se publica sin tu clic.</div>
              {candidatos.length === 0
                ? <div style={{ textAlign:'center', padding:'30px 10px', color:'#6B7280', fontSize:'12px' }}>Sin candidatos nuevos por ahora.</div>
                : candidatos.map((c,i) => {
                  const ts = TAG_STYLE_CM[c.tag] || TAG_STYLE_CM.MANUAL;
                  return (
                    <div key={i} style={{ display:'flex', gap:'12px', alignItems:'flex-start', padding:'13px 4px', borderTop: i===0 ? 'none' : '1px solid rgba(255,255,255,0.07)' }}>
                      <div style={{ flex:'0 0 auto', width:'32px', height:'32px', borderRadius:'9px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px', background:ts.bg, color:ts.color }}>{c.icono}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:'12.5px', fontWeight:'600', color:'#EDEFF7' }}>{c.titulo}</div>
                        {c.descripcion && <div style={{ fontSize:'11.5px', color:'#9BA3B4', marginTop:'2px', lineHeight:'1.4' }}>{c.descripcion}</div>}
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:'8px' }}>
                          <span style={{ fontSize:'10px', color:'#9BA3B4' }}>fuente: {c.fuente}</span>
                          <button onClick={() => publicarCandidato(c)} style={{ background:'#2A7FFF', color:'white', border:'none', fontSize:'11px', fontWeight:'700', padding:'6px 11px', borderRadius:'8px', cursor:'pointer' }}>Publicar →</button>
                        </div>
                      </div>
                    </div>
                  );
                })
              }
            </div>

            <div style={{ background:'linear-gradient(180deg, #20242c 0%, #262b34 100%)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'18px', padding:'18px 18px 10px' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'4px' }}>
                <h2 style={{ margin:0, fontSize:'14px', fontWeight:'700', color:'#EDEFF7' }}>📰 Publicado en ALQOS News</h2>
                <span style={{ fontSize:'11px', fontWeight:'700', color:'#EDEFF7', background:'rgba(255,255,255,.06)', padding:'2px 9px', borderRadius:'999px' }}>{publicadosOrdenados.length} visibles</span>
              </div>
              <div style={{ fontSize:'11px', color:'#9BA3B4', margin:'-2px 0 12px' }}>Esto es lo que ve todo el equipo.</div>
              <div style={{ marginBottom:'12px' }}>
                <button onClick={() => setShowFormNoticia(true)} style={{ width:'100%', padding:'9px', borderRadius:'10px', border:'1px dashed rgba(255,255,255,0.18)', background:'transparent', color:'#9BA3B4', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>+ Agregar novedad manual</button>
              </div>
              {publicadosOrdenados.length === 0
                ? <div style={{ textAlign:'center', padding:'30px 10px', color:'#6B7280', fontSize:'12px' }}>Todavía no publicaste nada.</div>
                : publicadosOrdenados.map((n,i) => {
                  const ts = TAG_STYLE_CM[n.tag] || TAG_STYLE_CM.MANUAL;
                  const totalReacciones = Object.keys(n.reacciones || {}).length;
                  return (
                    <div key={n.id} style={{ display:'flex', gap:'12px', alignItems:'flex-start', padding:'13px 4px', borderTop: i===0 ? 'none' : '1px solid rgba(255,255,255,0.07)' }}>
                      <div style={{ flex:'0 0 auto', width:'32px', height:'32px', borderRadius:'9px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px', background:ts.bg, color:ts.color }}>{n.icono}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:'12.5px', fontWeight:'600', color:'#EDEFF7' }}>{n.titulo}</div>
                        <div style={{ fontSize:'10px', color:'#9BA3B4', marginTop:'2px' }}>publicado por {n.publicadoPor}</div>
                        {totalReacciones > 0 && <div style={{ fontSize:'11px', color:'#9BA3B4', marginTop:'8px' }}>{totalReacciones} reacción{totalReacciones===1?'':'es'}</div>}
                      </div>
                    </div>
                  );
                })
              }
            </div>
          </div>
        </div>
      )}

      {showFormNoticia && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'white', borderRadius:'16px', padding:'24px', width:'420px', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize:'15px', fontWeight:'700', color:'#1C3FAA', marginBottom:'16px' }}>📣 Nueva novedad manual</div>
            <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
              <div><label style={lbl}>Título *</label><input style={inp} placeholder="Ej: Cumpleaños del equipo de Calidad" value={formNoticia.titulo} onChange={e => setFormNoticia(f=>({...f,titulo:e.target.value}))} /></div>
              <div><label style={lbl}>Descripción</label><textarea style={{ ...inp, minHeight:'60px', resize:'vertical' }} placeholder="Detalle..." value={formNoticia.descripcion} onChange={e => setFormNoticia(f=>({...f,descripcion:e.target.value}))} /></div>
              <div><label style={lbl}>Tag</label>
                <select style={inp} value={formNoticia.tag} onChange={e => setFormNoticia(f=>({...f,tag:e.target.value}))}>
                  {Object.keys(TAG_STYLE_CM).map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Ícono</label>
                <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                  {EMOJIS_NOTICIA.map(e => (
                    <button key={e} type="button" onClick={() => setFormNoticia(f=>({...f,icono:e}))}
                      style={{ width:'34px', height:'34px', fontSize:'16px', borderRadius:'8px', cursor:'pointer',
                        border: formNoticia.icono===e ? '2px solid #1C3FAA' : '1px solid #E5E7EB',
                        background: formNoticia.icono===e ? '#E8E9F6' : 'white' }}>
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display:'flex', gap:'10px', marginTop:'16px' }}>
              <button onClick={() => { setShowFormNoticia(false); setFormNoticia(NOTICIA_VACIA); }} style={{ flex:1, padding:'10px', borderRadius:'10px', border:'1px solid #E5E7EB', background:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer', color:'#6B7280' }}>Cancelar</button>
              <button onClick={publicarNoticiaManual} style={{ flex:2, padding:'10px', borderRadius:'10px', border:'none', background:'#1C3FAA', color:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>Publicar novedad</button>
            </div>
          </div>
        </div>
      )}

      {showFormTarea && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'white', borderRadius:'16px', padding:'24px', width:'460px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize:'15px', fontWeight:'700', color:'#1C3FAA', marginBottom:'16px' }}>📌 Nueva tarea de calidad</div>
            <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
              <div><label style={lbl}>Título *</label><input style={inp} placeholder="Ej: Revisar FMEAs proceso impresión" value={form.titulo} onChange={e => setForm(f=>({...f,titulo:e.target.value}))} /></div>
              <div><label style={lbl}>Descripción</label><textarea style={{ ...inp, minHeight:'60px', resize:'vertical' }} placeholder="Detalle de la tarea..." value={form.descripcion} onChange={e => setForm(f=>({...f,descripcion:e.target.value}))} /></div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                <div><label style={lbl}>Módulo vinculado</label>
                  <select style={inp} value={form.modulo} onChange={e => setForm(f=>({...f,modulo:e.target.value}))}>
                    {MODULOS_VINCULO.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div><label style={lbl}>Responsable</label><input style={inp} placeholder="Nombre" value={form.responsable} onChange={e => setForm(f=>({...f,responsable:e.target.value}))} /></div>
              </div>
              <div><label style={lbl}>Prioridad</label>
                <div style={{ display:'flex', gap:'8px' }}>
                  {Object.entries(PRIORIDADES).map(([k,{label,color,bg}]) => (
                    <button key={k} onClick={() => setForm(f=>({...f,prioridad:k}))}
                      style={{ flex:1, padding:'7px', borderRadius:'8px', border:`1.5px solid ${form.prioridad===k?color:'#E5E7EB'}`, background:form.prioridad===k?bg:'white', color:form.prioridad===k?color:'#6B7280', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>{label}</button>
                  ))}
                </div>
              </div>
              <div><label style={lbl}>Fecha límite</label><input type="date" style={inp} value={form.fechaLimite} onChange={e => setForm(f=>({...f,fechaLimite:e.target.value}))} /></div>
            </div>
            <div style={{ display:'flex', gap:'10px', marginTop:'16px' }}>
              <button onClick={() => setShowFormTarea(false)} style={{ flex:1, padding:'10px', borderRadius:'10px', border:'1px solid #E5E7EB', background:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer', color:'#6B7280' }}>Cancelar</button>
              <button onClick={agregarTarea} style={{ flex:2, padding:'10px', borderRadius:'10px', border:'none', background:'#1C3FAA', color:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>Guardar tarea</button>
            </div>
          </div>
        </div>
      )}

      {showFormSticker && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'white', borderRadius:'16px', padding:'24px', width:'380px', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize:'15px', fontWeight:'700', color:'#1C3FAA', marginBottom:'16px' }}>📌 Nuevo post-it</div>
            <div style={{ marginBottom:'12px' }}>
              <label style={lbl}>Color</label>
              <div style={{ display:'flex', gap:'8px' }}>
                {COLORES_STICKER.map(c => (
                  <button key={c} onClick={() => setStickerColor(c)}
                    style={{ width:'28px', height:'28px', borderRadius:'8px', background:c, border:stickerColor===c?'2px solid #1C3FAA':'2px solid transparent', cursor:'pointer' }} />
                ))}
              </div>
            </div>
            <div style={{ marginBottom:'16px' }}>
              <label style={lbl}>Texto</label>
              <textarea style={{ ...inp, minHeight:'100px', resize:'vertical' }} placeholder="Escribí el recordatorio..." value={stickerTexto} onChange={e => setStickerTexto(e.target.value)} />
            </div>
            <div style={{ background:stickerColor, borderRadius:'10px', padding:'12px', marginBottom:'16px', fontSize:'12px', color:'#374151' }}>
              <div style={{ fontSize:'10px', color:'rgba(55,65,81,0.5)', marginBottom:'4px' }}>Vista previa</div>
              {stickerTexto || 'Tu nota acá...'}
            </div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={() => setShowFormSticker(false)} style={{ flex:1, padding:'10px', borderRadius:'10px', border:'1px solid #E5E7EB', background:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer', color:'#6B7280' }}>Cancelar</button>
              <button onClick={agregarSticker} style={{ flex:2, padding:'10px', borderRadius:'10px', border:'none', background:'#1C3FAA', color:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>Pegar post-it</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
